import { useState } from "react";
import { adminApi } from "@/lib/adminApi";

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

function describeUA(ua = "") {
  const mobile = /Mobile|Android|iPhone|iPad/i.test(ua);
  const browser = /Edg\//.test(ua) ? "Edge" : /OPR\//.test(ua) ? "Opera" : /Chrome/.test(ua) ? "Chrome" : /Firefox/.test(ua) ? "Firefox" : /Safari/.test(ua) ? "Safari" : "Other";
  const os = /Windows/.test(ua) ? "Windows" : /Mac OS/.test(ua) ? "macOS" : /Android/.test(ua) ? "Android" : /iPhone|iPad|iOS/.test(ua) ? "iOS" : /Linux/.test(ua) ? "Linux" : "Other";
  return `${browser} / ${os} / ${mobile ? "Mobile" : "Desktop"}`;
}

function VisitorRow({ adminKey, visit }) {
  const [open, setOpen] = useState(false);
  const [timeline, setTimeline] = useState(null);
  const [error, setError] = useState("");

  const toggle = async () => {
    if (!open && !timeline) {
      try {
        const d = await adminApi.visits(adminKey, visit.visitor_id);
        setTimeline(d.visits);
      } catch (e) {
        setError(e.message);
      }
    }
    setOpen(!open);
  };

  return (
    <>
      <tr
        data-testid={`visit-row-${visit.visitor_id}`}
        onClick={toggle}
        className="cursor-pointer border-b border-white/10 text-sm text-white/80 transition-colors hover:bg-white/[0.03]"
      >
        <td className="py-3 pr-4 font-mono text-xs">{fmt(visit.created_at)}</td>
        <td className="py-3 pr-4 text-xs text-fog">{describeUA(visit.user_agent)}</td>
        <td className="py-3 pr-4 font-mono text-xs text-fog">{visit.ip || "—"}</td>
        <td className="py-3 pr-4 max-w-[200px] truncate text-xs text-fog">{visit.referrer || "direct"}</td>
        <td className="py-3 pr-4 font-mono text-xs text-fog">{String(visit.visitor_id).slice(0, 8)}</td>
        <td className="py-3 text-xs">
          {visit.name ? (
            <span className="text-accent">{visit.name}{visit.company ? ` — ${visit.company}` : ""}</span>
          ) : (
            <span className="text-fog">anonymous</span>
          )}
        </td>
      </tr>
      {open && (
        <tr className="border-b border-white/10 bg-ink-900">
          <td colSpan="6" className="px-4 py-4">
            {error ? (
              <p className="font-mono text-xs text-red-400">{error}</p>
            ) : !timeline ? (
              <p data-testid={`visit-timeline-${visit.visitor_id}`} className="font-mono text-xs text-fog">LOADING TIMELINE…</p>
            ) : (
              <div data-testid={`visit-timeline-${visit.visitor_id}`}>
                <p className="font-mono text-[10px] tracking-[0.25em] text-fog">FULL HISTORY — {timeline.length} SESSION{timeline.length === 1 ? "" : "S"}</p>
                <ul className="mt-3 flex flex-col gap-1">
                  {timeline.map((s, i) => (
                    <li key={s.id || i} className="font-mono text-xs text-white/70">
                      {fmt(s.created_at)} — {s.path || "/"} — {s.referrer || "direct"}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

export default function Visitors({ adminKey }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  if (!data && !error) {
    adminApi
      .visits(adminKey)
      .then(setData)
      .catch((e) => setError(e.message));
  }

  if (error) {
    return <p role="alert" data-testid="visitors-error" className="mt-10 font-mono text-xs text-red-400">{error}</p>;
  }
  if (!data) {
    return <p data-testid="visitors-loading" className="mt-10 font-mono text-xs text-fog">LOADING…</p>;
  }

  return (
    <div data-testid="admin-visitors" className="mt-10">
      <p data-testid="visitors-summary" className="font-mono text-xs text-fog">
        {data.total} TOTAL VISITS · {data.unique_visitors} UNIQUE VISITORS · CLICK A ROW FOR THE FULL HISTORY
      </p>
      <div className="mt-6 overflow-x-auto border-t border-white/10">
        <table className="w-full min-w-[760px] text-left">
          <thead>
            <tr className="border-b border-white/10 font-mono text-[10px] tracking-[0.2em] text-fog">
              <th className="py-3 pr-4">WHEN</th>
              <th className="py-3 pr-4">DEVICE</th>
              <th className="py-3 pr-4">IP</th>
              <th className="py-3 pr-4">REFERRER</th>
              <th className="py-3 pr-4">VISITOR</th>
              <th className="py-3">WHO (IF KNOWN)</th>
            </tr>
          </thead>
          <tbody>
            {data.visits.length === 0 ? (
              <tr><td colSpan="6" data-testid="visitors-empty" className="py-8 text-sm text-fog">No visits recorded yet.</td></tr>
            ) : (
              data.visits.map((v, i) => <VisitorRow key={v.id || i} adminKey={adminKey} visit={v} />)
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
